import React, { useEffect, useMemo, useState } from "react";
import AdminPageHeader from "../../components/AdminPageHeader";
import useUserRole from "../../hooks/useUserRole";
import useRolePermissions from "../../hooks/useRolePermissions";
import PrintableTimecard from "../../components/timecards/PrintableTimecard";
import { db, punchEntriesRef } from "../../lib/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import {
  isExcluded,
  isNonTrinityPayroll,
  isTrinityPayroll,
  sortEmployeesAlphabetically,
} from "../../utils/timecardUtils";

const FALLBACK_TIMECARD_CONFIG = {
  department: "80001",
  accountNumber: "670500",
  businessUnit: "32001",
  supervisorName: "Patrick Gauthier",
  supervisorUid: "AzDsDKAgT8cpqJSgJ3iBBt6AKmd2",
};

function startOfWeekMonday(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const day = x.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  x.setDate(x.getDate() + diff);
  return x;
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function endOfWeekSunday(d) {
  const mon = startOfWeekMonday(d);
  const sun = addDays(mon, 6);
  sun.setHours(23, 59, 59, 999);
  return sun;
}

function formatDateInputValue(d) {
  const x = new Date(d);
  const yyyy = x.getFullYear();
  const mm = String(x.getMonth() + 1).padStart(2, "0");
  const dd = String(x.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function fmtRange(d) {
  const start = startOfWeekMonday(d);
  const end = endOfWeekSunday(d);
  return `${start.toLocaleDateString()} – ${end.toLocaleDateString()}`;
}

function fmtWeekLabel(d) {
  return fmtRange(d);
}

function toDate(ts) {
  if (!ts) return null;
  if (typeof ts?.toDate === "function") return ts.toDate();
  if (ts?.seconds) return new Date(ts.seconds * 1000);
  return new Date(ts);
}

function fmtDay(d) {
  return d.toLocaleDateString(undefined, { weekday: "long" });
}

function fmtTime(d) {
  return d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function hoursBetween(start, end) {
  if (!start || !end) return 0;
  return Math.max(0, (end.getTime() - start.getTime()) / 3600000);
}

function getDisplayName(user) {
  return (
    user?.displayName ||
    user?.name ||
    user?.applicationData?.name ||
    "Unnamed Employee"
  );
}

function buildFullAddress(user = {}) {
  const app = user.applicationData || {};

  const street =
    user.street ||
    user.streetAddress ||
    user.address1 ||
    user.address ||
    app.street ||
    app.streetAddress ||
    app.address ||
    "";

  const city = user.city || app.city || "";
  const state = user.state || app.state || "";
  const zip =
    user.zip ||
    user.zipCode ||
    user.zipcode ||
    app.zip ||
    app.zipCode ||
    app.zipcode ||
    "";

  const cityStateZip = [[city, state].filter(Boolean).join(", "), zip]
    .filter(Boolean)
    .join(" ");

  return [street, cityStateZip].filter(Boolean).join(" ").trim();
}

function getProviderLevel(user) {
  return (
    String(user?.payTitle || "").trim() ||
    user?.basePosition ||
    user?.providerLevel ||
    user?.position ||
    "—"
  );
}

function getSignatureUrl(user) {
  return (
    user?.signatureURL ||
    user?.signatureUrl ||
    user?.signatureImageURL ||
    user?.signatureImageUrl ||
    user?.signaturePngURL ||
    user?.signaturePngUrl ||
    ""
  );
}

function buildRowsFromEntryIntervals(entryDocs, payRate, providerLevel) {
  const shifts = [...entryDocs]
    .map((p) => {
      const start = toDate(p.startTime) || null;
      const end = toDate(p.endTime) || null;
      return { ...p, start, end };
    })
    .filter((p) => p.start && p.end)
    .sort((a, b) => a.start.getTime() - b.start.getTime());

  return shifts.map((s) => {
    const hours =
      typeof s.hoursWorked === "number"
        ? Number(s.hoursWorked.toFixed(2))
        : Number(hoursBetween(s.start, s.end).toFixed(2));

    const rate =
      typeof s.rateUsed === "number"
        ? Number(s.rateUsed.toFixed(2))
        : payRate;

    const pay =
      typeof rate === "number"
        ? Number((hours * rate).toFixed(2))
        : null;

    return {
      date: new Date(
        s.start.getFullYear(),
        s.start.getMonth(),
        s.start.getDate()
      ),
      day: fmtDay(s.start),
      timeIn: fmtTime(s.start),
      timeOut: fmtTime(s.end),
      hours,
      pay,
      providerLevel,
    };
  });
}

function getTotals(rows) {
  const totalHours = rows.reduce((sum, r) => sum + (Number(r.hours) || 0), 0);
  const totalPay = rows.reduce((sum, r) => sum + (Number(r.pay) || 0), 0);

  return {
    totalHours: Number(totalHours.toFixed(2)),
    totalPay: Number(totalPay.toFixed(2)),
  };
}

function getNumericPayRate(user, payConfig) {
  const position = user?.position || "";
  const base = payConfig?.positions?.[position];

  if (typeof base !== "number") return null;

  let rate = Number(base);
  const bump = Number(payConfig?.supervisorBump || 0);

  if (String(user?.role || "").toLowerCase() === "supervisor") {
    rate += bump;
  }

  return rate;
}

function getYearStart(year) {
  return new Date(year, 0, 1, 0, 0, 0, 0);
}

function getYearEnd(year) {
  return new Date(year, 11, 31, 23, 59, 59, 999);
}

function groupRowsByWeek(rows = []) {
  const byWeek = new Map();

  for (const row of rows) {
    const weekStart = startOfWeekMonday(new Date(row.date));
    const key = formatDateInputValue(weekStart);

    if (!byWeek.has(key)) {
      byWeek.set(key, {
        weekStart,
        rows: [],
      });
    }

    byWeek.get(key).rows.push(row);
  }

  return [...byWeek.values()]
    .sort((a, b) => a.weekStart.getTime() - b.weekStart.getTime())
    .map((group) => ({
      weekStart: group.weekStart,
      rows: group.rows,
      totals: getTotals(group.rows),
    }));
}

function sortCards(cards = []) {
  return [...cards].sort((a, b) => {
    const aName = String(a?.user?.displayName || "").toLowerCase();
    const bName = String(b?.user?.displayName || "").toLowerCase();

    if (aName !== bName) return aName.localeCompare(bName);

    const aWeek = a?.weekStart ? new Date(a.weekStart).getTime() : 0;
    const bWeek = b?.weekStart ? new Date(b.weekStart).getTime() : 0;
    return aWeek - bWeek;
  });
}

function matchesSearch(card, searchTerm) {
  const q = String(searchTerm || "").trim().toLowerCase();

  if (!q) return true;

  const name = String(
    card?.user?.displayName ||
    card?.user?.name ||
    card?.user?.applicationData?.name ||
    ""
  ).toLowerCase();

  return name.includes(q);
}

function getUniqueEmployeeCount(cards = []) {
  return new Set(cards.map((card) => String(card?.user?.uid || card?.user?.id || ""))).size;
}

function BatchSummaryCard({
  title,
  count,
  warnings,
  onPrint,
  disabled,
  subtitle,
  buttonLabel = "Print / Save PDF",
}) {
  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        borderRadius: 14,
        padding: 16,
        background: "#fff",
      }}
    >
      <div style={{ fontWeight: 900, fontSize: 18 }}>{title}</div>
      <div style={{ marginTop: 6, color: "#475569" }}>
        {typeof subtitle === "string" && subtitle.length > 0
          ? subtitle
          : `${count} employee${count === 1 ? "" : "s"}`}
      </div>

      {warnings ? (
        <div style={{ marginTop: 12, display: "grid", gap: 6, fontSize: 14 }}>
          <div>Missing employee signature: {warnings.missingEmployeeSignature}</div>
          <div>Missing employee number: {warnings.missingEmployeeNumber}</div>
          <div>Missing hours: {warnings.noHours}</div>
        </div>
      ) : null}

      <div style={{ marginTop: 14 }}>
        <button
          className="btn btn-primary"
          onClick={onPrint}
          disabled={disabled}
        >
          {buttonLabel}
        </button>
      </div>
    </div>
  );
}

function ScreenCardShell({ title, subtitle, children, actions = null }) {
  return (
    <div
      style={{
        border: "1px solid #dbe4ee",
        borderRadius: 14,
        background: "#fff",
        overflow: "hidden",
        marginBottom: 24,
      }}
    >
      <div
        className="admin-timecards-no-print"
        style={{
          padding: 14,
          borderBottom: "1px solid #e2e8f0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div style={{ fontWeight: 900, fontSize: 16 }}>{title}</div>
          {subtitle ? (
            <div style={{ color: "#64748b", marginTop: 4, fontSize: 14 }}>
              {subtitle}
            </div>
          ) : null}
        </div>
        {actions}
      </div>

      {children}
    </div>
  );
}

export default function TimecardsAdmin() {
  const { role, loading: roleLoading } = useUserRole();
  const { can, loading: permsLoading } = useRolePermissions(role);

  const thisYear = new Date().getFullYear();

  const [weekStartValue, setWeekStartValue] = useState(() =>
    formatDateInputValue(startOfWeekMonday(new Date()))
  );
  const [calendarYear, setCalendarYear] = useState(thisYear);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [err, setErr] = useState("");
  const [timecardConfig, setTimecardConfig] = useState(FALLBACK_TIMECARD_CONFIG);
  const [supervisorSignature, setSupervisorSignature] = useState("");
  const [trinityCards, setTrinityCards] = useState([]);
  const [nonTrinityCards, setNonTrinityCards] = useState([]);
  const [excludedPeople, setExcludedPeople] = useState([]);
  const [printJob, setPrintJob] = useState(null);
  const [warnings, setWarnings] = useState({
    trinity: {
      missingEmployeeSignature: 0,
      missingEmployeeNumber: 0,
      noHours: 0,
    },
    nonTrinity: {
      missingEmployeeSignature: 0,
      missingEmployeeNumber: 0,
      noHours: 0,
    },
    supervisorMissingSignature: false,
  });

  const weekStart = useMemo(
    () => startOfWeekMonday(new Date(`${weekStartValue}T00:00:00`)),
    [weekStartValue]
  );
  const weekEnd = useMemo(() => endOfWeekSunday(weekStart), [weekStart]);

  const filteredTrinityCards = useMemo(
    () => trinityCards.filter((card) => matchesSearch(card, searchTerm)),
    [trinityCards, searchTerm]
  );

  const filteredNonTrinityCards = useMemo(
    () => nonTrinityCards.filter((card) => matchesSearch(card, searchTerm)),
    [nonTrinityCards, searchTerm]
  );

  const renderedTrinityCards = printJob
    ? printJob.trinityCards
    : filteredTrinityCards;

  const renderedNonTrinityCards = printJob
    ? printJob.nonTrinityCards
    : filteredNonTrinityCards;

  useEffect(() => {
    if (roleLoading || permsLoading) return;
    if (!can("TimeCardsRead")) return;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStartValue, roleLoading, permsLoading]);

  async function loadSharedData() {
    const cfgSnap = await getDoc(doc(db, "appConfig", "timecards"));
    const cfg = cfgSnap.exists()
      ? { ...FALLBACK_TIMECARD_CONFIG, ...cfgSnap.data() }
      : FALLBACK_TIMECARD_CONFIG;

    const payRatesSnap = await getDoc(doc(db, "appConfig", "payRates"));
    const payConfig = payRatesSnap.exists() ? payRatesSnap.data() : {};

    const supervisorSnap = await getDoc(doc(db, "users", cfg.supervisorUid));
    const supervisorData = supervisorSnap.exists() ? supervisorSnap.data() : {};
    const supSig = getSignatureUrl(supervisorData);

    const usersSnap = await getDocs(collection(db, "users"));
    const users = usersSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const activeUsers = users.filter((u) => u.active !== false);

    const excluded = activeUsers
      .filter((u) => isExcluded(String(u?.position || "")))
      .map((u) => ({
        id: u.id,
        displayName: getDisplayName(u),
        position: u?.position || "",
      }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));

    return {
      cfg,
      payConfig,
      supSig,
      activeUsers,
      excluded,
    };
  }

  async function buildCardsForRange({
    activeUsers,
    payConfig,
    rangeStart,
    rangeEnd,
    onlyUid = "",
    includeWarnings = false,
  }) {
    const trinityOut = [];
    const nonTrinityOut = [];

    const nextWarnings = {
      trinity: {
        missingEmployeeSignature: 0,
        missingEmployeeNumber: 0,
        noHours: 0,
      },
      nonTrinity: {
        missingEmployeeSignature: 0,
        missingEmployeeNumber: 0,
        noHours: 0,
      },
    };

    for (const user of activeUsers) {
      const position = String(user?.position || "");
      if (isExcluded(position)) continue;

      const uid = String(user?.uid || user?.id || "").trim();
      if (!uid) continue;
      if (onlyUid && uid !== onlyUid) continue;

      const payRate = getNumericPayRate(user, payConfig);
      const providerLevel = getProviderLevel(user);

      const punchesQ = query(
        punchEntriesRef(uid),
        where("startTime", ">=", rangeStart),
        where("startTime", "<=", rangeEnd),
        orderBy("startTime", "asc"),
        limit(2500)
      );

      const punchesSnap = await getDocs(punchesQ);
      const entryDocs = punchesSnap.docs
  .map((d) => ({
    id: d.id,
    ...d.data(),
  }))
  .filter((p) => !p.deleted);
      const rows = buildRowsFromEntryIntervals(entryDocs, payRate, providerLevel);
      const weekGroups = groupRowsByWeek(rows);

      const fullAddress = buildFullAddress(user);

    const normalizedUser = {
  ...user,
  uid,
  displayName: getDisplayName(user),
  fullAddress,
  address: fullAddress,
  providerLevel,
  signatureURL: getSignatureUrl(user),
  payRate,
  payRateDisplay:
    typeof payRate === "number" ? `$${payRate.toFixed(2)}/hr` : "",
};

      if (isTrinityPayroll(normalizedUser)) {
        if (includeWarnings && !normalizedUser.signatureURL) {
          nextWarnings.trinity.missingEmployeeSignature += 1;
        }
        if (
          includeWarnings &&
          !String(
            normalizedUser.employeeNumber ||
              normalizedUser.empNumber ||
              normalizedUser.employeeId ||
              ""
          ).trim()
        ) {
          nextWarnings.trinity.missingEmployeeNumber += 1;
        }
        if (weekGroups.length === 0) {
          if (includeWarnings) nextWarnings.trinity.noHours += 1;
          continue;
        }

        for (const group of weekGroups) {
          trinityOut.push({
            user: normalizedUser,
            rows: group.rows,
            totals: group.totals,
            weekStart: group.weekStart,
          });
        }
        continue;
      }

      if (isNonTrinityPayroll(normalizedUser)) {
        if (includeWarnings && !normalizedUser.signatureURL) {
          nextWarnings.nonTrinity.missingEmployeeSignature += 1;
        }
        if (weekGroups.length === 0) {
          if (includeWarnings) nextWarnings.nonTrinity.noHours += 1;
          continue;
        }

        for (const group of weekGroups) {
          nonTrinityOut.push({
            user: normalizedUser,
            rows: group.rows,
            totals: group.totals,
            weekStart: group.weekStart,
          });
        }
      }
    }

    return {
      trinityCards: sortCards(trinityOut),
      nonTrinityCards: sortCards(nonTrinityOut),
      warnings: nextWarnings,
    };
  }

  async function loadData() {
    try {
      setLoading(true);
      setErr("");

      const { cfg, payConfig, supSig, activeUsers, excluded } =
        await loadSharedData();

      setTimecardConfig(cfg);
      setSupervisorSignature(supSig);
      setExcludedPeople(excluded);

      const built = await buildCardsForRange({
        activeUsers,
        payConfig,
        rangeStart: weekStart,
        rangeEnd: weekEnd,
        includeWarnings: true,
      });

      setTrinityCards(built.trinityCards);
      setNonTrinityCards(built.nonTrinityCards);
      setWarnings({
        ...built.warnings,
        supervisorMissingSignature: !supSig,
      });
    } catch (e) {
      console.error("TimecardsAdmin load error:", e);
      setErr(e?.message || "Could not load timecards.");
      setTrinityCards([]);
      setNonTrinityCards([]);
      setExcludedPeople([]);
    } finally {
      setLoading(false);
    }
  }

  async function runPrintJob(nextPrintJob) {
    setPrintJob(nextPrintJob);

    await new Promise((resolve) => setTimeout(resolve, 200));

    document.body.classList.add("admin-timecards-printing");
    window.print();

    setTimeout(() => {
      document.body.classList.remove("admin-timecards-printing");
      setPrintJob(null);
    }, 500);
  }

  async function handlePrintCurrentWeeklyBatch(batchType) {
    const trinityToPrint =
      batchType === "trinity" ? filteredTrinityCards : [];
    const nonTrinityToPrint =
      batchType === "nonTrinity" ? filteredNonTrinityCards : [];

    if (trinityToPrint.length === 0 && nonTrinityToPrint.length === 0) return;

    await runPrintJob({
      label:
        batchType === "trinity"
          ? `Trinity Weekly Batch • ${fmtRange(weekStart)}`
          : `Non-Trinity Weekly Batch • ${fmtRange(weekStart)}`,
      trinityCards: trinityToPrint,
      nonTrinityCards: nonTrinityToPrint,
    });
  }

  async function handlePrintEmployeeYear(user) {
    try {
      setPrintLoading(true);
      setErr("");

      const { cfg, payConfig, supSig, activeUsers } = await loadSharedData();

      setTimecardConfig(cfg);
      setSupervisorSignature(supSig);

      const built = await buildCardsForRange({
        activeUsers,
        payConfig,
        rangeStart: getYearStart(calendarYear),
        rangeEnd: getYearEnd(calendarYear),
        onlyUid: String(user?.uid || user?.id || "").trim(),
      });

      if (built.trinityCards.length === 0 && built.nonTrinityCards.length === 0) {
        setErr(
          `No timecards found for ${user.displayName || "this employee"} in ${calendarYear}.`
        );
        return;
      }

      await runPrintJob({
        label: `${user.displayName} • ${calendarYear} Weekly Timecards`,
        trinityCards: built.trinityCards,
        nonTrinityCards: built.nonTrinityCards,
      });
    } catch (e) {
      console.error("Employee year print error:", e);
      setErr(e?.message || "Could not build employee year timecards.");
    } finally {
      setPrintLoading(false);
    }
  }

  async function handlePrintFullYearBatch() {
    try {
      setPrintLoading(true);
      setErr("");

      const { cfg, payConfig, supSig, activeUsers } = await loadSharedData();

      setTimecardConfig(cfg);
      setSupervisorSignature(supSig);

      const built = await buildCardsForRange({
        activeUsers,
        payConfig,
        rangeStart: getYearStart(calendarYear),
        rangeEnd: getYearEnd(calendarYear),
      });

      if (built.trinityCards.length === 0 && built.nonTrinityCards.length === 0) {
        setErr(`No timecards found for calendar year ${calendarYear}.`);
        return;
      }

      await runPrintJob({
        label: `Full Calendar Year Batch • ${calendarYear}`,
        trinityCards: built.trinityCards,
        nonTrinityCards: built.nonTrinityCards,
      });
    } catch (e) {
      console.error("Full year batch print error:", e);
      setErr(e?.message || "Could not build yearly payroll batch.");
    } finally {
      setPrintLoading(false);
    }
  }

  const pageTitle = `Time Cards • ${fmtRange(weekStart)}`;
  const visibleWeeklyEmployeeCount =
    getUniqueEmployeeCount(filteredTrinityCards) +
    getUniqueEmployeeCount(filteredNonTrinityCards);

  if (roleLoading || permsLoading) {
    return <div style={{ padding: 20 }}>Loading…</div>;
  }

  if (!can("TimeCardsRead")) {
    return (
      <div style={{ padding: 20 }}>
        <AdminPageHeader title="Time Cards" />
        <p>You do not have access to this page.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: 20 }}>
      <div className="admin-timecards-no-print">
        <AdminPageHeader title="Time Cards" />

        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "end",
            flexWrap: "wrap",
            marginBottom: 16,
          }}
        >
          <div>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Pay Period Start</div>
            <input
              type="date"
              value={weekStartValue}
              onChange={(e) =>
                setWeekStartValue(
                  formatDateInputValue(
                    startOfWeekMonday(new Date(`${e.target.value}T00:00:00`))
                  )
                )
              }
            />
          </div>

          <button
            className="btn"
            onClick={() =>
              setWeekStartValue(
                formatDateInputValue(
                  addDays(new Date(`${weekStartValue}T00:00:00`), -7)
                )
              )
            }
          >
            Prev Week
          </button>

          <button
            className="btn"
            onClick={() =>
              setWeekStartValue(formatDateInputValue(startOfWeekMonday(new Date())))
            }
          >
            This Week
          </button>

          <button
            className="btn"
            onClick={() =>
              setWeekStartValue(
                formatDateInputValue(
                  addDays(new Date(`${weekStartValue}T00:00:00`), 7)
                )
              )
            }
          >
            Next Week
          </button>

          <button className="btn btn-primary" onClick={loadData} disabled={loading}>
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>

        <div style={{ marginBottom: 16, color: "#475569" }}>{pageTitle}</div>

        <div
          style={{
            border: "1px solid #e2e8f0",
            borderRadius: 14,
            padding: 16,
            background: "#fff",
            marginBottom: 16,
          }}
        >
          <div style={{ fontWeight: 900, marginBottom: 8 }}>Timecard Config</div>
          <div>Department: {timecardConfig.department}</div>
          <div>Account Number: {timecardConfig.accountNumber}</div>
          <div>Business Unit: {timecardConfig.businessUnit || "32001"}</div>
          <div>Supervisor: {timecardConfig.supervisorName}</div>
          <div>
            Supervisor Signature:{" "}
            {warnings.supervisorMissingSignature ? "Missing" : "On File"}
          </div>
        </div>

        <div
          style={{
            border: "1px solid #e2e8f0",
            borderRadius: 14,
            padding: 16,
            background: "#fff",
            marginBottom: 16,
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 12,
              alignItems: "end",
            }}
          >
            <div>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>
                Search employee
              </div>
              <input
                className="form-input"
                type="text"
                placeholder="Type any part of a name"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>
                Calendar Year for Year Prints
              </div>
              <input
                className="form-input"
                type="number"
                min="2024"
                max="2100"
                step="1"
                value={calendarYear}
                onChange={(e) => {
                  const next = Number(e.target.value || thisYear);
                  setCalendarYear(Number.isFinite(next) ? next : thisYear);
                }}
              />
            </div>

            <div>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Visible This Week</div>
              <div style={{ color: "#475569", paddingBottom: 12 }}>
                {visibleWeeklyEmployeeCount} employee
                {visibleWeeklyEmployeeCount === 1 ? "" : "s"} matched
              </div>
            </div>

            <div>
              <button
                className="btn"
                onClick={handlePrintFullYearBatch}
                disabled={printLoading}
              >
                {printLoading
                  ? "Preparing…"
                  : `Print Full Year Batch (${calendarYear})`}
              </button>
            </div>
          </div>
        </div>

        {err ? (
          <div style={{ marginBottom: 16, color: "#b91c1c", fontWeight: 800 }}>
            {err}
          </div>
        ) : null}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 16,
            marginBottom: 20,
          }}
        >
          <BatchSummaryCard
            title="Trinity Payroll Batch"
            count={getUniqueEmployeeCount(filteredTrinityCards)}
            subtitle={`${filteredTrinityCards.length} weekly timecard${
              filteredTrinityCards.length === 1 ? "" : "s"
            } visible`}
            warnings={warnings.trinity}
            onPrint={() => handlePrintCurrentWeeklyBatch("trinity")}
            disabled={filteredTrinityCards.length === 0 || printLoading}
          />

          <BatchSummaryCard
            title="Non-Trinity Payroll Batch"
            count={getUniqueEmployeeCount(filteredNonTrinityCards)}
            subtitle={`${filteredNonTrinityCards.length} weekly timecard${
              filteredNonTrinityCards.length === 1 ? "" : "s"
            } visible`}
            warnings={warnings.nonTrinity}
            onPrint={() => handlePrintCurrentWeeklyBatch("nonTrinity")}
            disabled={filteredNonTrinityCards.length === 0 || printLoading}
          />

          <div
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: 14,
              padding: 16,
              background: "#fff",
            }}
          >
            <div style={{ fontWeight: 900, fontSize: 18 }}>Excluded</div>
            <div style={{ marginTop: 6, color: "#475569" }}>
              {excludedPeople.length} excluded by position
            </div>
            <div style={{ marginTop: 12, maxHeight: 160, overflowY: "auto" }}>
              {excludedPeople.length === 0 ? (
                <div style={{ color: "#64748b" }}>None</div>
              ) : (
                excludedPeople.map((p) => (
                  <div key={p.id} style={{ fontSize: 14, marginBottom: 4 }}>
                    {p.displayName} — {p.position}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <div id="admin-timecards-print-area">
        {printJob?.label ? (
          <div
            className="admin-timecards-no-print"
            style={{ marginBottom: 16, color: "#475569", fontWeight: 700 }}
          >
            {printJob.label}
          </div>
        ) : null}

        {renderedTrinityCards.length > 0 && (
          <div style={{ marginBottom: 28 }}>
            {!printJob ? (
              <div
                className="admin-timecards-no-print"
                style={{
                  fontWeight: 900,
                  fontSize: 20,
                  marginBottom: 12,
                  color: "#0f172a",
                }}
              >
                Trinity Timecards
              </div>
            ) : null}

            {renderedTrinityCards.map((card) => {
              const cardKey = `${card.user.id}-${formatDateInputValue(card.weekStart)}`;

              return (
                <ScreenCardShell
                  key={cardKey}
                  title={card.user.displayName}
                  subtitle={fmtWeekLabel(card.weekStart)}
                  actions={
                    !printJob ? (
                      <button
                        className="btn btn-secondary"
                        onClick={() => handlePrintEmployeeYear(card.user)}
                        disabled={printLoading}
                      >
                        {printLoading
                          ? "Preparing…"
                          : `Print ${calendarYear} Year Cards`}
                      </button>
                    ) : null
                  }
                >
                  <PrintableTimecard
                    user={card.user}
                    rows={card.rows}
                    totals={card.totals}
                    weekStart={card.weekStart}
                    supervisorSignature={supervisorSignature}
                    department={timecardConfig.department}
                    accountNumber={timecardConfig.accountNumber}
                    businessUnit={timecardConfig.businessUnit || "32001"}
                    supervisorName={timecardConfig.supervisorName}
                  />
                </ScreenCardShell>
              );
            })}
          </div>
        )}

        {renderedNonTrinityCards.length > 0 && (
          <div>
            {!printJob ? (
              <div
                className="admin-timecards-no-print"
                style={{
                  fontWeight: 900,
                  fontSize: 20,
                  marginBottom: 12,
                  color: "#0f172a",
                }}
              >
                Non-Trinity Timecards
              </div>
            ) : null}

            {renderedNonTrinityCards.map((card) => {
              const cardKey = `${card.user.id}-${formatDateInputValue(card.weekStart)}`;

              return (
                <ScreenCardShell
                  key={cardKey}
                  title={card.user.displayName}
                  subtitle={fmtWeekLabel(card.weekStart)}
                  actions={
                    !printJob ? (
                      <button
                        className="btn btn-secondary"
                        onClick={() => handlePrintEmployeeYear(card.user)}
                        disabled={printLoading}
                      >
                        {printLoading
                          ? "Preparing…"
                          : `Print ${calendarYear} Year Cards`}
                      </button>
                    ) : null
                  }
                >
                  <PrintableTimecard
                    user={card.user}
                    rows={card.rows}
                    totals={card.totals}
                    weekStart={card.weekStart}
                    supervisorSignature={supervisorSignature}
                    department={timecardConfig.department}
                    accountNumber={timecardConfig.accountNumber}
                    businessUnit={timecardConfig.businessUnit || "32001"}
                    supervisorName={timecardConfig.supervisorName}
                  />
                </ScreenCardShell>
              );
            })}
          </div>
        )}

        {renderedTrinityCards.length === 0 && renderedNonTrinityCards.length === 0 && !loading ? (
          <div
            className="admin-timecards-no-print"
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: 14,
              padding: 18,
              background: "#fff",
              color: "#64748b",
            }}
          >
            No timecards found for the current filters.
          </div>
        ) : null}
      </div>
    </div>
  );
}